import { useLocation, useMatchRoute } from "@tanstack/react-router";
import type { RegisteredRouter } from "@tanstack/react-router";
import type { RouteToPath } from "@tanstack/router-core";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

import type { Link } from "../NestedNavBar/types";
import { NavGroupContent } from "./NavGroupContent";
import { NavGroupIcon } from "./NavGroupIcon";

export interface NavGroupContextValue {
  isActive: boolean;
}

export interface NavGroupProps {
  children: ReactNode;
  groupLinks?: Link[];
}

const NavGroupContext = createContext<NavGroupContextValue | null>(null);
NavGroupContext.displayName = "NavGroupContext";

export const useNavContext = (): NavGroupContextValue => {
  const context = useContext(NavGroupContext);

  if (!context) {
    throw new Error(
      "useNavContext must be used within a NavGroupContext.Provider",
    );
  }

  return context;
};

export const NavGroup = ({ children, groupLinks = [] }: NavGroupProps) => {
  const matchRoute = useMatchRoute();
  const pathname = useLocation({ select: location => location.pathname });

  const isActive = useMemo(() => {
    if (groupLinks.length === 0) {
      return false;
    }

    return groupLinks.some(link =>
      Boolean(
        matchRoute({
          to: link.path as RouteToPath<RegisteredRouter>,
          fuzzy: !link.exact,
        }),
      ),
    );
  }, [groupLinks, matchRoute, pathname]);

  const value = {
    isActive,
  };

  return (
    <NavGroupContext.Provider value={value}>
      <div>{children}</div>
    </NavGroupContext.Provider>
  );
};

NavGroup.Icon = NavGroupIcon;
NavGroup.Content = NavGroupContent;
